import {
  Applicant,
  ApplicantScreening,
  ScreeningType,
  ScreeningVendor,
} from "src/types/Applicant";
import { convertToLocalAndFormatDate, formatTime } from "./Utils";
import {
  Issue,
  ScreeningStatusId,
  MapScreeningEnumTypeToScreeningStatusId,
  ScreeningDocumentResult,
  ScreeningStatus,
  ScreeningStatusMap,
} from "src/types/Screening";
import { GroupScreeningStatusDetails } from "src/types/Application";

/**
 * Helper function for sorting dates w/ consideration
 *      over the createdOn and modifiedDate
 */
const sortByDateDesc = (a, b) => {
  const dateA = new Date(a.modifiedDate || a.createdOn);
  const dateB = new Date(b.modifiedDate || b.createdOn);

  if (dateA.getTime() > dateB.getTime()) {
    return -1;
  }
  if (dateA.getTime() < dateB.getTime()) {
    return 1;
  }
  return 0;
};

const mapScreeningDocumentResult = (
  screeningStatusId: string,
  noOfAttempts: number
): string => {
  switch (screeningStatusId) {
    case "Approved":
      if (noOfAttempts > 0) {
        return ScreeningDocumentResult.resubmittedPassed;
      }
      return ScreeningDocumentResult.passed;
    case "Retry":
      if (noOfAttempts > 0) {
        return ScreeningDocumentResult.resubmittedInsufficient;
      }
      return ScreeningDocumentResult.insufficientDocumentation;
    case "FurtherReview": // when applicant's snappt screening lands in FurtherReview status, it means that they have exhausted all their attempts and snappt is asking for additional documentation from the applicant, so we should render the result as "Resubmitted - Insufficient"
      return ScreeningDocumentResult.resubmittedInsufficient;
    case "Declined":
      if (noOfAttempts > 0) {
        return ScreeningDocumentResult.resubmittedFailed;
      }
      return ScreeningDocumentResult.failed;
    default:
      return "-";
  }
};

/**
 * Helper function for exposing the onfido and fadv screening status,
 *  as well as the most recent update dated.
 */

export interface ScreeningInfo {
  lastUpdated: string;
  screeningStatus: string;
  screeningStatusDetails?: string | null;
  screeningVendor: string;
  issues?: Issue[];
}

interface ScreeningStatuses {
  [key: string]: any;
  onfido?: ScreeningInfo;
  fadv?: ScreeningInfo;
  transunion?: ScreeningInfo;
}

const normalizeString = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
};

export const getGroupScreeningStatusMessage = (
  groupScreeningStatusDetails?: string | GroupScreeningStatusDetails | null,
  fallbackStatusLabel?: string | null
): string => {
  if (typeof groupScreeningStatusDetails === "string") {
    return normalizeString(groupScreeningStatusDetails);
  }

  if (!groupScreeningStatusDetails) {
    return normalizeString(fallbackStatusLabel);
  }

  const message =
    normalizeString(groupScreeningStatusDetails.message) ||
    normalizeString(
      groupScreeningStatusDetails["Message"] as string | null | undefined
    );
  const description =
    normalizeString(groupScreeningStatusDetails.description) ||
    normalizeString(
      groupScreeningStatusDetails["Description"] as string | null | undefined
    );
  const statusLabel =
    normalizeString(groupScreeningStatusDetails.statusLabel) ||
    normalizeString(
      groupScreeningStatusDetails["StatusLabel"] as string | null | undefined
    ) ||
    normalizeString(groupScreeningStatusDetails.status) ||
    normalizeString(
      groupScreeningStatusDetails["Status"] as string | null | undefined
    );

  return (
    message ||
    description ||
    statusLabel ||
    normalizeString(fallbackStatusLabel)
  );
};

const GUARANTOR_ONLY_ELIGIBLE_GROUP_OUTCOME_IDS = [
  ScreeningStatusId.ApprovedWithConditions,
  ScreeningStatusId.Conditional,
];

const GUARANTOR_ONLY_ELIGIBLE_GROUP_OUTCOMES =
  GUARANTOR_ONLY_ELIGIBLE_GROUP_OUTCOME_IDS.map(
    (screeningStatusId) => ScreeningStatusMap[screeningStatusId]
  );

export const shouldShowGuarantorOnlySecondaryLabel = ({
  isGuarantorOnly,
  adminDashApplicationScreeningResultId,
  adminDashApplicationScreeningResult,
}: {
  isGuarantorOnly?: boolean | null;
  adminDashApplicationScreeningResultId?: number | null;
  adminDashApplicationScreeningResult?: string | null;
}): boolean => {
  if (!isGuarantorOnly) {
    return false;
  }

  if (
    typeof adminDashApplicationScreeningResultId === "number" &&
    GUARANTOR_ONLY_ELIGIBLE_GROUP_OUTCOME_IDS.includes(
      adminDashApplicationScreeningResultId
    )
  ) {
    return true;
  }

  if (typeof adminDashApplicationScreeningResult !== "string") {
    return false;
  }

  return GUARANTOR_ONLY_ELIGIBLE_GROUP_OUTCOMES.includes(
    adminDashApplicationScreeningResult.trim()
  );
};

export const getScreeningStatuses = (
  [...applicantScreening]: ApplicantScreening[],
  setLatestDate
): ScreeningStatuses | undefined => {
  const hasApplicantScreeningData =
    applicantScreening && applicantScreening.length;

  const statuses: ScreeningStatuses = {};
  let latestDate: string | null = null;

  if (hasApplicantScreeningData) {
    // sort descending by dates
    applicantScreening.sort(sortByDateDesc);

    const hasTransUnionCredit = applicantScreening.some(
      (screening) =>
        !screening.isSoftDeleted &&
        !!screening.screeningVendor &&
        String(screening.screeningVendor).toLowerCase().includes("transunion")
    );
    const hasFadvCredit = applicantScreening.some(
      (screening) =>
        !screening.isSoftDeleted &&
        !!screening.screeningVendor &&
        String(screening.screeningVendor).toLowerCase().includes("fadv")
    );
    const expectedCreditVendor: "transunion" | "fadv" | null =
      hasTransUnionCredit ? "transunion" : hasFadvCredit ? "fadv" : null;

    // since this is sorted in descending order
    // the most updated dates should be available first
    applicantScreening.sort(sortByDateDesc).forEach((screening, idx) => {
      const {
        screeningVendor,
        createdOn,
        modifiedDate,
        isSoftDeleted,
        issues,
        screeningType,
        screeningStatusDetails,
        noOfAttempts,
      } = screening;
      let { screeningStatus } = screening;

      const hasExpectedCreditStatus = expectedCreditVendor
        ? !!statuses[expectedCreditVendor]
        : !!statuses["fadv"] || !!statuses["transunion"];
      const isComplete = !!statuses["onfido"] && hasExpectedCreditStatus;

      // if no vendor or already complete, end fast
      if (!screeningVendor || isComplete || isSoftDeleted) return;

      const vendor = screeningVendor.toLowerCase();

      // Keep the first (most recent) status per vendor from the sorted list.
      if (statuses[vendor]) return;

      // capture latest date
      if (idx === 0) {
        latestDate = modifiedDate || createdOn;
      }

      // For Identity screening:
      // when applicant is declined for first attempt, applicant is allowed to retry once, so status should render as Retry
      // when applicant opens onfido window for 2nd attempt, screening status lands in "Other" status, but status should render as Retry

      if (screeningType === ScreeningType.IDENTITY) {
        if (
          screeningStatus === ScreeningStatus.Declined &&
          noOfAttempts === 0
        ) {
          screeningStatus = ScreeningStatus.Retry;
        } else if (
          screeningStatus === ScreeningStatus.Other &&
          noOfAttempts > 0
        ) {
          screeningStatus = ScreeningStatus.Retry;
        }
      }

      // format
      statuses[vendor] = {
        lastUpdated: modifiedDate || createdOn,
        screeningStatus:
          MapScreeningEnumTypeToScreeningStatusId[screeningStatus],
        screeningStatusDetails,
        screeningVendor,
        issues: issues ?? [],
      };
    });

    statuses["latestDate"] = latestDate;
    if (latestDate) {
      const formattedDate = `${formatTime(
        latestDate
      )}, ${convertToLocalAndFormatDate(latestDate)}`;
      setLatestDate(formattedDate);
    }
    return statuses;
  }
};

export const getDocumentScreeningData = (
  applicant: Applicant,
  [...applicantScreening]: ApplicantScreening[]
) => {
  const hasApplicantScreeningData =
    applicantScreening && applicantScreening.length;
  let documentScreening = {
    emailAddress: applicant.emailAddress,
    firstName: applicant.firstName,
    lastName: applicant.lastName,
    snapptStatus: "Not Started",
    result: "",
    isFurtherReview: false,
    issues: "",
    documents: "",
    lastUpdated: "",
    id: applicant.id,
    isSoftDelete: applicant.isSoftDelete,
  };
  if (hasApplicantScreeningData) {
    // get latest snappt screening
    const snapptScreening = applicantScreening
      .sort(sortByDateDesc)
      .filter(
        (screening) => screening.screeningVendor === ScreeningVendor.SNAPPT
      );

    if (snapptScreening.length) {
      documentScreening = {
        emailAddress: applicant.emailAddress,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        snapptStatus:
          snapptScreening[0]?.screeningStatus !== "Pending"
            ? "Ready"
            : "Pending",
        result: mapScreeningDocumentResult(
          snapptScreening[0]?.screeningStatus,
          snapptScreening[0].noOfAttempts
        ),
        isFurtherReview:
          snapptScreening[0]?.screeningStatus === "FurtherReview",
        issues:
          snapptScreening[0]?.issues.length > 0
            ? snapptScreening[0]?.issues[0].description
            : "",
        documents:
          snapptScreening[0]?.noOfAttempts != null
            ? (snapptScreening[0]?.noOfAttempts * 2 + 2).toString()
            : "-",
        lastUpdated:
          snapptScreening.length > 0
            ? snapptScreening[0].modifiedDate ?? snapptScreening[0].createdOn
            : "-",
        id: applicant.id,
        isSoftDelete: applicant.isSoftDelete,
      };
    }
  }
  return documentScreening;
};

/**
 * Returns true if the applicant has at least one non-soft-deleted
 * FADV or TransUnion credit screening record that is not in Error status.
 *
 * Soft-deleted records must be excluded because they represent
 * cancelled/retried submissions and should not block the
 * "Submit again" action. Error-status records must also be excluded:
 * associates are allowed to resubmit indefinitely after a screening
 * error, so an Error record should not be treated as an active
 * screening blocking resubmission.
 */
export const hasActiveFadvOrTransUnionScreening = (
  applicantScreening: ApplicantScreening[] | null | undefined
): boolean => {
  if (!applicantScreening || applicantScreening.length === 0) return false;
  return applicantScreening.some((s) => {
    if (s.isSoftDeleted) return false;
    if (s.screeningStatus === ScreeningStatus.Error) return false;
    const vendor = String(s.screeningVendor).toUpperCase();
    return (
      vendor === ScreeningVendor.FADV.toUpperCase() ||
      vendor === ScreeningVendor.TRANSUNION.toUpperCase()
    );
  });
};
